// IMAGE_RECORDING — Section 10.
//
// NOTE on prompt_text: the schema (Section 4/12) defines content/images/ as
// plain JPGs with no per-image metadata file, unlike texts which each have
// a JSON sidecar. That matches the iOS pilot's design (HANDOFF.md: "a
// Beatles photo with fixed prompt") — one standardized prompt question is
// asked for every image, only the photo itself cycles. That prompt is read
// from content-manifest's optional `image_prompt_text` field (falls back to
// the example in Section 5) rather than invented per-image.

import { useEffect, useRef, useState } from 'react';
import { AudioRecorder } from '../audio/recorder';
import RecordingIndicator from '../components/RecordingIndicator';
import StopButton from '../components/StopButton';
import ConfirmationModal from '../components/ConfirmationModal';
import { useSessionStore } from '../store/sessionStore';
import { useParticipantStore } from '../store/participantStore';
import { getManifest, resolveContentUrl } from '../utils/content';

const DEFAULT_PROMPT = 'How will you describe this to a blind person with all the details?';

export default function ImageRecordingScreen({ onContinue, onAbandon }) {
  const contentIndex = useSessionStore((s) => s.contentIndex);
  const textTrial = useSessionStore((s) => s.textTrial);
  const setImageTrial = useSessionStore((s) => s.setImageTrial);
  const abandon = useSessionStore((s) => s.abandon);
  const participantPatch = useParticipantStore((s) => s.patch);

  const recorderRef = useRef(null);
  const cueOnsetRef = useRef(null);
  const [image, setImage] = useState(null);
  const [promptText, setPromptText] = useState(DEFAULT_PROMPT);
  const [error, setError] = useState(null);
  const [confirmingDone, setConfirmingDone] = useState(false);
  const [confirmingStop, setConfirmingStop] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const manifest = await getManifest();
      if (manifest?.image_prompt_text) setPromptText(manifest.image_prompt_text);
      const entry = manifest?.images?.[contentIndex % (manifest.images.length || 1)];
      if (cancelled) return;
      if (!entry) {
        setError('This image is not available offline. Reconnect and try again.');
        return;
      }
      setImage(entry);
      cueOnsetRef.current = new Date().toISOString();

      try {
        const recorder = new AudioRecorder();
        await recorder.start();
        recorderRef.current = recorder;
        setStartedAt(recorder.startedAt);
      } catch {
        setError('Microphone access is required to continue. Please allow microphone access and reopen the app.');
      }
    })();
    return () => {
      cancelled = true;
      recorderRef.current?.abort();
    };
  }, [contentIndex]);

  async function handleDone() {
    const recorder = recorderRef.current;
    const blob = await recorder.stop();
    setImageTrial({
      trial_id: crypto.randomUUID(),
      content_key: image.key,
      image_filename: image.filename ?? `${image.key}.jpg`,
      prompt_text: promptText,
      cue_onset_ts_utc: cueOnsetRef.current,
      recording_start_ts_utc: recorder.startedAt,
      recording_end_ts_utc: recorder.endedAt,
      duration_ms: new Date(recorder.endedAt) - new Date(recorder.startedAt),
      blob,
    });
    onContinue();
  }

  async function handleStop() {
    // Abandoning here discards BOTH audio blobs — including the already-
    // completed text recording — per spec: "Stop button ... discard both
    // blobs". The text blob only ever lived in sessionStore (not yet
    // persisted to IndexedDB), so dropping the store on abandon is enough.
    recorderRef.current?.abort();
    abandon('image_recording');
    await participantPatch({ active_in_session: false, current_session_id: null });
    onAbandon();
  }

  if (error) {
    return (
      <div className="screen justify-center text-center">
        <p className="text-stop mb-6">{error}</p>
        <StopButton label="Back" onClick={() => setConfirmingStop(true)} />
        <ConfirmationModal
          open={confirmingStop}
          title="Stop this session?"
          message="Nothing will be saved from this session, including the reading you already recorded."
          confirmLabel="Yes, stop"
          cancelLabel="No, keep going"
          danger
          onCancel={() => setConfirmingStop(false)}
          onConfirm={handleStop}
        />
      </div>
    );
  }

  return (
    <div className="screen">
      <h1 className="font-display text-2xl mb-4">Speak about this image</h1>

      <div className="mb-4">{startedAt && <RecordingIndicator startedAt={startedAt} />}</div>

      <div className="rounded-xl2 overflow-hidden border border-hairline mb-4" style={{ maxHeight: '55vh' }}>
        {image ? (
          <img
            src={resolveContentUrl(`content/images/${image.filename ?? `${image.key}.jpg`}`)}
            alt=""
            className="w-full h-full object-contain bg-panel"
          />
        ) : (
          <div className="h-56 flex items-center justify-center text-dim">Loading image…</div>
        )}
      </div>

      <p className="text-lg text-center mb-6">{promptText}</p>

      <button type="button" disabled={!image} onClick={() => setConfirmingDone(true)} className="btn-primary mt-auto">
        Finish
      </button>
      <StopButton onClick={() => setConfirmingStop(true)} />

      <ConfirmationModal
        open={confirmingDone}
        title="Done speaking?"
        message="Moving to the check-in survey."
        confirmLabel="Yes"
        cancelLabel="No, keep going"
        onCancel={() => setConfirmingDone(false)}
        onConfirm={() => {
          setConfirmingDone(false);
          handleDone();
        }}
      />
      <ConfirmationModal
        open={confirmingStop}
        title="Stop this session?"
        message="Nothing will be saved from this session, including the reading you already recorded."
        confirmLabel="Yes, stop"
        cancelLabel="No, keep going"
        danger
        onCancel={() => setConfirmingStop(false)}
        onConfirm={handleStop}
      />
    </div>
  );
}
